import { providerKeyData } from "@src/utility/common.service2";
import {
    checkGeoFlow,
    checkHCO,
    checkIsInternal,
    checkIsSimulationMode,
    checkMultiTin,
    checkSingleTin,
} from '@src/utility/createStorage';
import HttpService from '@src/utility/httpService';
import { config } from '@uhg-abyss/web/tools/config';
import { MULTI_TIN } from '@src/apps/PriorAuth/constant';

const _httpService = new HttpService();
const cache = new Map();

if (checkIsInternal()) {
    _httpService?.setBaseURL(config('API_DOMAIN_INTERNAL') || '');
} else {
    _httpService?.setBaseURL(config('API_DOMAIN_EXTERNAL') || '');
}

export const MARKET_URL = '/claim-search/topfilter';
export const TAX_STATE_REGION_URL = '/standard-search/topfilter';
export const TOP_FILTER_URL = '/standard-search/topfilter';
export const NPI_FLOW = 'NPI';
export const TIN_FLOW = 'TIN';
export const REQUESTED_JOURNEY = 'priorAuthFilter';
export const NPS_DRIVER_FILTER = 'npsDriverFilter';
export const PSMS_FILTER_JOURNEY = 'psmsFilter';
export const STATE_TEXT = 'STATE';
export const MARKET_TEXT = 'MARKET';
export const MARKET_NAME = 'MARKETNAME';
export const REGION_STATE_INITIAL = 'REGION_STATE_INITIAL';
export const EXTENDED_TIN_DETAIL_URL = '/extended-tins/top-filter?items=MULTITIN';
export const PSNMS_EXTENDED_TIN_DETAIL_JOURNEY = 'provTinListFilterPSMS';

export const getGeoData = async (flow, url, CurrentVals, geoSelectedVals) => {
    const payload = await getPSMSFilterPayload(
        flow,
        CurrentVals,
        geoSelectedVals
    );
    try {
        const response: any = await _httpService.push(url, payload);
        if ((response as any)?.Data) {
            if (flow === REGION_STATE_INITIAL) {
                const RegionList = (response as any).Data?.RegionList.map(
                    (region: string) => {
                        const camelCaseRegion = region
                            .toLowerCase()
                            .replace(/(?:^|\s|_)(\w)/g, (_, c) => c.toUpperCase());
                        return {
                            id: region,
                            Name: camelCaseRegion,
                            value: camelCaseRegion,
                            displayName: camelCaseRegion,
                        };
                    }
                );

                const StateList = (response as any).Data?.StateList.map(
                    (state: any) => ({
                        id: state.StateAbbr,
                        Name: state.StateName,
                        value: state.StateName,
                        displayName: state.StateName,
                    })
                );


                // Sort both RegionList and StateList accordingly
                RegionList.sort((a: any, b: any) => {
                    const regionA = a.displayName || '';
                    const regionB = b.displayName || '';
                    return regionA.localeCompare(regionB, undefined, { numeric: true });
                });

                StateList.sort((a: any, b: any) => {
                    const stateA = a.displayName || '';
                    const stateB = b.displayName || '';
                    return stateA.localeCompare(stateB, undefined, { numeric: true });
                });

                return { RegionList, StateList };
            } else if (flow === MARKET_TEXT) {
                const MarketList = (response as any).Data.map((market: any) => ({
                    id: market?.Name,
                    Name: market?.Name,
                    value: market?.Name,
                    displayName: market?.Name,
                }));

                // Sort MarketList accordingly
                MarketList.sort((a: any, b: any) => {
                    const marketA = a.displayName || '';
                    const marketB = b.displayName || '';
                    return marketA.localeCompare(marketB, undefined, { numeric: true });
                });
                return { MarketList };
            }
        }
    } catch {
        return null;
    }
};

export const getPSMSFilterPayload = async (
    currentFlow,
    currentVal?: any,
    selectedVal?: any
) => {
    let psmsPayload: any = {};
    const taxID = currentVal?.tin?.value?.toString();
    const providerKey = providerKeyData();
    const standardPeriods = [
        'Last30Days',
        'Last3Months',
        'Last6Months',
        'Last12Months',
        'YTD',
    ];

    // Get timeperiod value - prioritize .value property
    const timePeriodValue = currentVal?.timeperiod?.value || 'Last12Months';

    const isStandardPeriod = standardPeriods.includes(timePeriodValue);

    const timeFilter = isStandardPeriod ? timePeriodValue : 'CalendarYear';
    const timeFilterText = timePeriodValue;
    const multiTIn = sessionStorage.getItem('multiTinNavigation') === "true" ? true : false;
    // console.log("geo flow values", geoRegion, ' ', geoState, ' ', geoMarket);
    switch (currentFlow) {
        case MULTI_TIN: {
            psmsPayload = {
                RequestedJourney: '',
                PriorAuthFilter: {
                    TimeFilter: timeFilter,
                    TimeFilterText: timeFilterText,
                    ItemsBy: TIN_FLOW,
                    ProviderSysKey: "",
                    Tins: sessionStorage.getItem('multiSelectedTins') || '',
                },
            };
            break;
        }
        case TIN_FLOW: {
            psmsPayload = {
                RequestedJourney: '',
                PriorAuthFilter: {
                    ProviderSysKey: (checkIsInternal() && !checkIsSimulationMode()) ? providerKey : undefined,
                    ItemsBy: TIN_FLOW,
                    TimeFilter: timeFilter,
                    TimeFilterText: timeFilterText,
                    Tins: sessionStorage.getItem('multiTinNavigation') !== "true" ? selectedVal : sessionStorage.getItem('multiSelectedTins')

                },
            };
            break;
        }
        case REGION_STATE_INITIAL: {
            // Build payload for loading region and state options
            const region = selectedVal && selectedVal.length > 0 ? selectedVal[0] : undefined;
            const state = selectedVal && selectedVal.length > 1 ? selectedVal[1] : undefined;

            const affordabilityFilter: any = {
                TimeFilter: timeFilter,
                TimeFilterText: timeFilterText,
            };

            // Only include Region/State if they have values
            if (region) {
                affordabilityFilter.Region = region;
            }
            if (state) {
                affordabilityFilter.State = state;
            }

            psmsPayload = {
                RequestedJourney: NPS_DRIVER_FILTER,
                PriorAuthAffordabilityFilter: affordabilityFilter,
            };
            break;
        }
        case MARKET_TEXT: {
            // Build payload for loading market options
            const region = selectedVal && selectedVal.length > 0 ? selectedVal[0] : undefined;
            const state = selectedVal && selectedVal.length > 1 ? selectedVal.slice(1) : [];

            const priorAuthFilter: any = {
                TimeFilter: timeFilter,
                TimeFilterText: timeFilterText,
                ItemsBy: MARKET_NAME,
            };

            // Only include Region/State if they have values
            if (region) {
                priorAuthFilter.Region = region;
            }
            if (state.length > 0) {
                priorAuthFilter.State = state.join(',');
            }

            psmsPayload = {
                RequestedJourney: REQUESTED_JOURNEY,
                PriorAuthFilter: priorAuthFilter,
            };
            break;
        }
        default: {
            psmsPayload = {
                TimeFilterText: "2025",
                EntityId: "ENT0002053",
                IsPSMS: "1"
            }
        }
    }
    return psmsPayload;
};

export const getPSMSCommonPayload = async () => {
    // const providerKey = providerKeyData();  
}

export const getPSMSCurrentFlow = () => {
    if (checkSingleTin()) return 'SINGLE_TIN';
    if (checkMultiTin()) return 'MULTI_TIN';
    if (checkGeoFlow()) return 'GEO_FLOW';
    if (checkHCO()) return 'HCO';
    return undefined;
};

/**
 * Get TIN list for selected entity from top filter API
 * @param entityId - The provider entity ID
 * @returns Promise with TIN list data
 */
export const getTopFilterTins = async (entityId: string) => {
    if (!entityId) {
        return null;
    }

    const payload = {
        RequestedJourney: PSMS_FILTER_JOURNEY,
        PsmsFilter: {
            EntityId: entityId
        }
    };

    try {
        const response: any = await _httpService.push(TOP_FILTER_URL, payload);
        if (response?.Data) {
            return response.Data;
        }
        return null;
    } catch (error) {
        console.error('Error calling top filter API:', error);
        return null;
    }
};

export const getExtendedTinDetail = async (tin: string) => {
    if (!tin) {
        return null;
    }

    const payload = {
        RequestedJourney: PSNMS_EXTENDED_TIN_DETAIL_JOURNEY,
        ProvTinListFilter: {
            Tin: tin
        }
    };

    try {
        const response: any = await _httpService.push(EXTENDED_TIN_DETAIL_URL, payload);
        if (response?.Data) {
            return response.Data;
        }
        return null;
    } catch (error) {
        console.error('Error calling extended TIN detail API:', error);
        return null;
    }
};

